'use client';

import type { ProductCategorySummary } from '@eliadmin/core/src/api/products/types/product-types';
import { Check, ChevronDown, ChevronRight, FolderOpen, Loader2, Search, X } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  buildFitmentCategoryIndex,
  buildFitmentCategoryOptions,
  FITMENT_CATEGORY_FILTER_LIMIT,
  getVisibleFitmentCategoryOptions,
  normalizeFitmentCategoryId,
  resolveFitmentCategorySelection,
  type FitmentCategoryOption,
} from './fitment-categories';

export function FitmentCategorySelect({
  categoryTree,
  value,
  isLoading,
  isError,
  onRetry,
  onChange,
}: {
  categoryTree?: ProductCategorySummary[];
  value: number[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChange: (categoryIds: number[]) => void;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const allCategoriesRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef(new Map<number, HTMLDivElement>());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [collapsedSearchIds, setCollapsedSearchIds] = useState<Set<number>>(new Set());
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [rejectedSelectionKey, setRejectedSelectionKey] = useState<string | null>(null);
  const index = useMemo(() => buildFitmentCategoryIndex(categoryTree), [categoryTree]);
  const options = useMemo(() => buildFitmentCategoryOptions(index), [index]);
  const selectedIds = useMemo(
    () => [
      ...new Set(value.map(normalizeFitmentCategoryId).filter((id): id is number => id !== null)),
    ],
    [value],
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedLabels = useMemo(
    () => new Map(options.map(option => [option.id, option.label])),
    [options],
  );
  const selectionKey = selectedIds.join(',');
  const selectionResolution = useMemo(
    () => resolveFitmentCategorySelection(selectedIds, index),
    [selectedIds, index],
  );
  const showLimitError =
    selectionResolution.status === 'too-many' || rejectedSelectionKey === selectionKey;
  const isSearching = search.trim().length > 0;
  const currentExpandedIds = useMemo(
    () =>
      isSearching
        ? new Set(
            options
              .filter(
                option =>
                  index.byId.get(option.id)?.childIds.length && !collapsedSearchIds.has(option.id),
              )
              .map(option => option.id),
          )
        : expandedIds,
    [isSearching, options, index, collapsedSearchIds, expandedIds],
  );
  const visibleOptions = useMemo(
    () => getVisibleFitmentCategoryOptions(options, index, currentExpandedIds, search),
    [options, index, currentExpandedIds, search],
  );
  const siblingPositions = useMemo(() => {
    const siblings = new Map<number | null, number[]>();
    for (const option of visibleOptions) {
      const parentId = index.byId.get(option.id)!.parentId;
      const ids = siblings.get(parentId) ?? [];
      ids.push(option.id);
      siblings.set(parentId, ids);
    }
    const positions = new Map<number, { position: number; size: number }>();
    for (const ids of siblings.values()) {
      ids.forEach((categoryId, position) =>
        positions.set(categoryId, { position: position + 1, size: ids.length }),
      );
    }
    return positions;
  }, [visibleOptions, index]);
  const activeId = visibleOptions.some(option => option.id === focusedId)
    ? focusedId
    : (visibleOptions[0]?.id ?? null);

  const changeSearch = (nextSearch: string) => {
    setSearch(nextSearch);
    setFocusedId(null);
    // Search reveals matches without changing the user's normal expanded branches.
    setCollapsedSearchIds(new Set());
  };

  const toggleBranch = (categoryId: number) => {
    const setExpanded = isSearching ? setCollapsedSearchIds : setExpandedIds;
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const focusItem = (categoryId: number | undefined) => {
    if (categoryId === undefined) return;
    setFocusedId(categoryId);
    itemRefs.current.get(categoryId)?.focus();
  };

  const focusClearControl = () => {
    const focusTarget = allCategoriesRef.current?.getClientRects().length
      ? allCategoriesRef.current
      : triggerRef.current;
    focusTarget?.focus();
  };

  const toggleCategory = (categoryId: number) => {
    const nextIds = selectedIdSet.has(categoryId)
      ? selectedIds.filter(id => id !== categoryId)
      : [...selectedIds, categoryId];
    if (resolveFitmentCategorySelection(nextIds, index).status === 'too-many') {
      setRejectedSelectionKey(selectionKey);
      return;
    }
    setRejectedSelectionKey(null);
    // Keep explicit checks independent; only the request resolver merges selected branches.
    onChange(nextIds);
    if (itemRefs.current.get(categoryId)?.getClientRects().length) focusItem(categoryId);
    else focusClearControl();
  };

  const clearSelection = () => {
    onChange([]);
    setRejectedSelectionKey(null);
    changeSearch('');
    // The inline tree stays open. On a collapsed mobile panel, return focus to its heading.
    focusClearControl();
  };

  const handleTreeKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    option: FitmentCategoryOption,
  ) => {
    const position = visibleOptions.findIndex(item => item.id === option.id);
    const node = index.byId.get(option.id)!;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(visibleOptions[Math.min(position + 1, visibleOptions.length - 1)]?.id);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(visibleOptions[Math.max(position - 1, 0)]?.id);
        break;
      case 'Home':
      case 'End':
        event.preventDefault();
        focusItem(visibleOptions[event.key === 'Home' ? 0 : visibleOptions.length - 1]?.id);
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (node.childIds.length > 0) {
          if (!currentExpandedIds.has(option.id)) toggleBranch(option.id);
          else
            focusItem(
              visibleOptions.find(item => index.byId.get(item.id)?.parentId === option.id)?.id,
            );
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (node.childIds.length > 0 && currentExpandedIds.has(option.id)) toggleBranch(option.id);
        else if (node.parentId !== null) focusItem(node.parentId);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        toggleCategory(option.id);
        break;
    }
  };

  return (
    <section
      className="min-w-0 overflow-hidden rounded-xl border border-border bg-background lg:flex lg:max-h-[calc(100dvh-8rem)] lg:flex-col"
      aria-labelledby={`${id}-label`}
    >
      <div className="shrink-0 p-4">
        <h3 id={`${id}-label`} className="text-base font-semibold">
          <span className="hidden lg:block">Filtrovanie podľa kategórie</span>
          <button
            ref={triggerRef}
            id={id}
            type="button"
            onClick={() => setMobileOpen(previous => !previous)}
            aria-expanded={mobileOpen}
            aria-controls={`${id}-panel`}
            className="flex min-h-11 w-full items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:hidden"
          >
            <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="flex-1">Filtrovanie podľa kategórie</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${mobileOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </h3>
        {selectedIds.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="font-medium">Vybrané kategórie</span>
              <button
                type="button"
                aria-label="Zrušiť všetky kategórie doplnkov"
                onClick={clearSelection}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <ul
              className="max-h-24 space-y-1 overflow-y-auto overscroll-contain"
              aria-label="Vybrané kategórie"
            >
              {selectedIds.map(categoryId => {
                const label = selectedLabels.get(categoryId) || `Kategória #${categoryId}`;
                return (
                  <li
                    key={categoryId}
                    className="flex min-w-0 items-center gap-1 rounded-lg bg-primary/5 pl-2 text-xs text-primary"
                  >
                    <span className="min-w-0 flex-1 truncate py-2" title={label}>
                      {label}
                    </span>
                    <button
                      type="button"
                      aria-label={`Odobrať kategóriu ${label}`}
                      onClick={() => toggleCategory(categoryId)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      <div
        id={`${id}-panel`}
        className={`border-t ${mobileOpen ? 'block' : 'hidden'} lg:flex lg:min-h-0 lg:flex-col`}
      >
        <div className="shrink-0 border-b p-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              ref={searchRef}
              type="search"
              value={search}
              aria-label="Hľadať kategóriu"
              aria-controls={options.length > 0 ? `${id}-tree` : undefined}
              placeholder="Hľadať kategóriu…"
              onChange={event => changeSearch(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  focusItem(
                    visibleOptions[event.key === 'ArrowDown' ? 0 : visibleOptions.length - 1]?.id,
                  );
                }
              }}
              className="h-11 w-full rounded-lg border bg-background pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        <div className="max-h-[min(26rem,60vh)] overflow-y-auto overscroll-contain p-2 lg:max-h-none lg:min-h-0">
          <button
            ref={allCategoriesRef}
            type="button"
            onClick={clearSelection}
            aria-pressed={selectedIds.length === 0}
            className={`mb-1 flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${selectedIds.length === 0 ? 'bg-primary/5 font-semibold text-primary' : ''}`}
          >
            <span className="flex-1">Všetky kategórie</span>
            {selectedIds.length === 0 && <Check className="h-4 w-4" aria-hidden="true" />}
          </button>
          {showLimitError && (
            <p
              role="alert"
              className="m-1 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm"
            >
              Naraz môžete vybrať najviac {FITMENT_CATEGORY_FILTER_LIMIT} samostatných kategórií.
              Zrušte niektorú voľbu alebo vyberte spoločnú nadradenú kategóriu.
            </p>
          )}
          {isError && (
            <div
              role="alert"
              className="m-1 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm"
            >
              <p>Kategórie sa nepodarilo načítať.</p>
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 rounded font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Skúsiť znova
              </button>
            </div>
          )}
          {isLoading && options.length === 0 ? (
            <p
              role="status"
              className="flex items-center gap-2 px-3 py-5 text-sm text-muted-foreground"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Načítavam kategórie…
            </p>
          ) : options.length === 0 ? (
            !isError && (
              <p className="px-3 py-4 text-sm text-muted-foreground">
                Kategórie zatiaľ nie sú dostupné.
              </p>
            )
          ) : (
            <div
              id={`${id}-tree`}
              role="tree"
              aria-label="Kategórie doplnkov"
              aria-multiselectable="true"
            >
              {visibleOptions.map(option => {
                const node = index.byId.get(option.id)!;
                const hasChildren = node.childIds.length > 0;
                const expanded = hasChildren && currentExpandedIds.has(option.id);
                const checked = selectedIdSet.has(option.id);
                const siblingPosition = siblingPositions.get(option.id)!;
                const name = node.category.name?.trim() || `Kategória #${node.id}`;
                return (
                  <div
                    key={option.id}
                    ref={element => {
                      if (element) itemRefs.current.set(option.id, element);
                      else itemRefs.current.delete(option.id);
                    }}
                    role="treeitem"
                    aria-label={option.label}
                    aria-level={node.depth + 1}
                    aria-posinset={siblingPosition.position}
                    aria-setsize={siblingPosition.size}
                    aria-expanded={hasChildren ? expanded : undefined}
                    aria-selected={checked}
                    tabIndex={activeId === option.id ? 0 : -1}
                    onFocus={() => setFocusedId(option.id)}
                    onKeyDown={event => handleTreeKeyDown(event, option)}
                    onClick={() => toggleCategory(option.id)}
                    className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-lg pr-3 text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${checked ? 'bg-primary/5 text-primary' : ''}`}
                    style={{ paddingLeft: 4 + Math.min(node.depth, 6) * 16 }}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`${expanded ? 'Zbaliť' : 'Rozbaliť'} ${name}`}
                        aria-expanded={expanded}
                        onMouseDown={event => event.preventDefault()}
                        onClick={event => {
                          event.stopPropagation();
                          toggleBranch(option.id);
                          focusItem(option.id);
                        }}
                        className="flex h-11 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-primary"
                      >
                        {expanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    ) : (
                      <span className="w-8 shrink-0" aria-hidden="true" />
                    )}
                    <input
                      type="checkbox"
                      checked={checked}
                      aria-label={option.label}
                      tabIndex={-1}
                      onMouseDown={event => event.preventDefault()}
                      onClick={event => event.stopPropagation()}
                      onChange={() => toggleCategory(option.id)}
                      className="h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-primary"
                    />
                    <span className="min-w-0 flex-1 py-2">
                      <span
                        className={`block break-words ${node.depth === 0 ? 'font-medium' : ''}`}
                      >
                        {name}
                        {option.label.endsWith(` (#${option.id})`) ? ` (#${option.id})` : ''}
                      </span>
                    </span>
                  </div>
                );
              })}
              {visibleOptions.length === 0 && (
                <div className="px-3 py-5 text-sm text-muted-foreground" role="status">
                  <p>Takúto kategóriu sme nenašli.</p>
                  <button
                    type="button"
                    onClick={() => {
                      changeSearch('');
                      searchRef.current?.focus();
                    }}
                    className="mt-2 rounded font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    Zrušiť hľadanie kategórie
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="shrink-0 border-t bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
          Nadradená kategória zahŕňa aj podkategórie. Zobrazíme doplnky z ktorejkoľvek označenej
          kategórie.
        </p>
      </div>
    </section>
  );
}
